import { F0Icon } from '@factorialco/f0-react'
import { F0Button } from '@factorialco/f0-react'
import { Stack, TwoColumnsList, Widget } from '@factorialco/f0-react/dist/experimental'
import { Warning } from '@factorialco/f0-react/icons/app'
import { differenceInDays, startOfDay } from 'date-fns'
import { DealDetailFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { SegmentedProgressChart } from 'modules/finance/components/Deals/SegmentedProgressChart'
import { computeDealProgress, dealTranslateStatus } from 'modules/finance/components/Deals/utils'

interface DealWidgetsProps {
  deal: DealDetailFragment
}

interface EstimatedValueWidgetProps extends DealWidgetsProps {}

interface DealProgressWidgetProps extends DealWidgetsProps {}

export const EstimatedValueWidget: React.FC<EstimatedValueWidgetProps> = ({ deal }) => {
  const {
    access: { locale },
  } = useCurrentSession()

  return (
    <Widget
      header={{
        title: i18n.t('finance.deals.details.estimated_deal_value'),
      }}
    >
      <h1 className="text-3xl font-semibold">
        {MoneyWithCurrency({
          amount: deal.estimatedAmountCents,
          currency: deal.currency,
          locale: locale,
        })}
      </h1>

      <TwoColumnsList
        list={[
          {
            info: deal.quotes.length || 0,
            title: i18n.t('finance.deals.details.total_quotes_sent'),
          },
          {
            info: deal.invoices.length || 0,
            title: i18n.t('finance.deals.details.total_invoices_sent'),
          },
        ]}
      />
    </Widget>
  )
}

export const DealProgressWidget: React.FC<DealProgressWidgetProps> = ({ deal }) => {
  const progress = computeDealProgress(deal)

  return (
    <Widget
      header={{
        title: i18n.t('finance.deals.details.deal_progress'),
      }}
    >
      <Stack gap="4">
        <h1 className="text-3xl font-semibold">{dealTranslateStatus(deal.stage)}</h1>
        <SegmentedProgressChart
          filled={progress.stageIndex}
          total={5}
          color={progress.progressColor}
        />
      </Stack>
      <TwoColumnsList
        list={[
          {
            info: progress.totalTimeInStageFormatted,
            title: i18n.t('finance.deals.details.total_time_in_stage'),
          },
          {
            info: progress.totalTimeInPipelineFormatted,
            title: i18n.t('finance.deals.details.total_time_in_pipeline'),
          },
        ]}
      />
    </Widget>
  )
}

type CloseDateStatus = 'no-date' | 'on-track' | 'at-risk' | 'overdue'

interface ExpectedCloseDateWidgetProps extends DealWidgetsProps {
  onAddCloseDate?: () => void
}

export const ExpectedCloseDateWidget: React.FC<ExpectedCloseDateWidgetProps> = ({
  deal,
  onAddCloseDate,
}) => {
  const today = startOfDay(new Date())
  const createdDate = startOfDay(new Date(deal.createdAt))
  const expectedDate = deal.expectedCloseDate ? startOfDay(new Date(deal.expectedCloseDate)) : null

  const daysPassedFromOpening = differenceInDays(today, createdDate)

  const daysToClose = expectedDate ? differenceInDays(expectedDate, createdDate) : 1

  const daysRemaining = expectedDate ? differenceInDays(expectedDate, today) : 0

  const getCloseDateStatus = (): CloseDateStatus => {
    if (!expectedDate) return 'no-date'
    if (daysRemaining < 0) return 'overdue'
    if (daysRemaining <= Math.ceil(daysToClose / 3)) return 'at-risk'
    return 'on-track'
  }

  const closeDateStatus = getCloseDateStatus()

  const getProgressColor = () => {
    switch (closeDateStatus) {
      case 'overdue':
        return 'critical-50'
      case 'at-risk':
        return 'warning-50'
      case 'no-date':
        return 'neutral-30'
      case 'on-track':
      default:
        return 'info-50'
    }
  }

  const progressColor = getProgressColor()

  return (
    <Widget
      header={{
        title: i18n.t('finance.deals.details.expected_close_date'),
      }}
    >
      <Stack gap="4">
        <>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-semibold">
              {deal.expectedCloseDate
                ? convertDate(deal.expectedCloseDate, 'backend_datetime', 'ui_dmy_long')
                : 'N/A'}
            </h1>
            {closeDateStatus === 'overdue' && <F0Icon icon={Warning} color="critical" />}
            {closeDateStatus === 'no-date' && onAddCloseDate && (
              <F0Button
                variant="outline"
                size="sm"
                onClick={onAddCloseDate}
                label={i18n.t('finance.deals.details.add_close_date')}
              />
            )}
          </div>

          <SegmentedProgressChart
            filled={daysPassedFromOpening}
            total={daysToClose}
            color={progressColor}
          />
        </>
      </Stack>
      <TwoColumnsList
        list={[
          {
            // TODO: Temp fix while F0 is fixing the basis
            info: convertDate(deal.createdAt, 'backend_datetime', 'ui_dmy_long')?.replace(
              / /g,
              '\u00A0'
            ),
            title: i18n.t('finance.deals.details.deal_created_at'),
          },
          {
            info: (() => {
              switch (closeDateStatus) {
                case 'no-date':
                  return '-'
                case 'overdue':
                  return Math.abs(daysRemaining)
                default:
                  return daysToClose - daysPassedFromOpening
              }
            })(),
            title: (() => {
              switch (closeDateStatus) {
                case 'no-date':
                  return i18n.t('finance.deals.details.days_to_close')
                case 'overdue':
                  return i18n.t('finance.deals.details.days_overdue')
                default:
                  return i18n.t('finance.deals.details.days_remaining')
              }
            })(),
          },
        ]}
      />
    </Widget>
  )
}
